import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Combobox } from '@/components/ui/combobox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  assetTechnologyOptions,
  companySections,
  getAssetTypeOptions,
  keyDateFields,
  lifecycleStatusOptions,
} from '@/lib/assets';

function Field({ label, children, className = '' }) {
  return (
    <div className={`space-y-2 ${className}`.trim()}>
      <Label className="text-sm font-medium text-slate-700">{label}</Label>
      {children}
    </div>
  );
}

export default function AssetProfileFormFields({
  form,
  users = [],
  portfolios = [],
  subPortfolios = [],
  companies = [],
  onChange,
  onTechnologyChange,
  onAssetManagerChange,
  onPortfolioChange,
  onSubPortfolioChange,
  onCompanyChange,
}) {
  const normalizeText = (value) => (value || '').trim().toLowerCase();
  const assetTypeOptions = getAssetTypeOptions(form.type);
  const matchedPortfolioByName =
    !form.portfolio_id && form.portfolio
      ? portfolios.find((record) => normalizeText(record.name) === normalizeText(form.portfolio))
      : null;
  const resolvedPortfolioId = form.portfolio_id || matchedPortfolioByName?.id || '';

  const matchedSubPortfolioByName =
    !form.sub_portfolio_id && form.sub_portfolio
      ? subPortfolios.find(
          (record) =>
            normalizeText(record.name) === normalizeText(form.sub_portfolio) &&
            (!resolvedPortfolioId || record.portfolio_id === resolvedPortfolioId)
        )
      : null;
  const resolvedSubPortfolioId = form.sub_portfolio_id || matchedSubPortfolioByName?.id || '';

  const portfolioLegacyValue = `legacy-portfolio::${form.portfolio || ''}`;
  const subPortfolioLegacyValue = `legacy-sub-portfolio::${form.sub_portfolio || ''}`;
  const portfolioHasRealSelection = Boolean(resolvedPortfolioId);
  const subPortfolioHasRealSelection = Boolean(resolvedSubPortfolioId);

  const hasPortfolioNameInOptions = portfolios.some(
    (record) => normalizeText(record.name) === normalizeText(form.portfolio)
  );
  const hasSubPortfolioNameInOptions = subPortfolios.some(
    (record) => normalizeText(record.name) === normalizeText(form.sub_portfolio)
  );

  const portfolioSelectValue = portfolioHasRealSelection
    ? resolvedPortfolioId
    : form.portfolio
      ? portfolioLegacyValue
      : 'unselected';
  const subPortfolioSelectValue = subPortfolioHasRealSelection
    ? resolvedSubPortfolioId
    : form.sub_portfolio
      ? subPortfolioLegacyValue
      : 'unselected';

  const availableSubPortfolios = subPortfolios.filter(
    (record) => !resolvedPortfolioId || record.portfolio_id === resolvedPortfolioId
  );

  const [countries, setCountries] = useState([]);
  const [statesList, setStatesList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/locations/countries`);
        setCountries(res.data.map(c => ({ label: c.name, value: c.name, id: c.id })));
      } catch (e) {
        console.error('Failed to fetch countries', e);
      }
    };
    fetchCountries();
  }, []);

  useEffect(() => {
    const fetchStates = async () => {
      if (!form.country || countries.length === 0) {
        setStatesList([]);
        return;
      }
      try {
        const countryObj = countries.find(c => c.label === form.country || c.value === form.country);
        if (!countryObj) return;
        
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/locations/states?country_id=${countryObj.id}`);
        setStatesList(res.data.map(s => ({ label: s.name, value: s.name, id: s.id })));
      } catch (e) {
        console.error('Failed to fetch states', e);
      }
    };
    fetchStates();
  }, [form.country, countries]);

  useEffect(() => {
    const fetchCities = async () => {
      if (!form.state_region || statesList.length === 0) {
        setCitiesList([]);
        return;
      }
      try {
        const stateObj = statesList.find(s => s.label === form.state_region || s.value === form.state_region);
        if (!stateObj) return;

        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/locations/cities?state_id=${stateObj.id}`);
        setCitiesList(res.data.map(c => ({ label: c.name, value: c.name, id: c.id })));
      } catch (e) {
        console.error('Failed to fetch cities', e);
      }
    };
    fetchCities();
  }, [form.state_region, statesList]);

  const handleCountryChange = (val) => {
    onChange('country', val);
    onChange('state_region', '');
    onChange('city', '');
  };

  const handleStateChange = (val) => {
    onChange('state_region', val);
    onChange('city', '');
  };

  const handleCityChange = (val) => {
    onChange('city', val);
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div>
          <h3 className="font-heading text-lg font-semibold text-slate-900">Asset Profile</h3>
          <p className="text-sm text-slate-500">Capture the commercial and operating profile for this asset.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <Field label="Asset ID *">
            <Input value={form.asset_id} onChange={(e) => onChange('asset_id', e.target.value)} />
          </Field>
          <Field label="Asset Name *">
            <Input value={form.name} onChange={(e) => onChange('name', e.target.value)} />
          </Field>
          <Field label="Lifecycle Status">
            <Select value={form.lifecycle_status} onValueChange={(value) => onChange('lifecycle_status', value)}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {lifecycleStatusOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Technology">
            <Select value={form.type} onValueChange={onTechnologyChange}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {assetTechnologyOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Asset Type">
            <Select value={form.asset_type} onValueChange={(value) => onChange('asset_type', value)}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {assetTypeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Asset Manager (Internal User)">
            <Select
              value={form.asset_manager_id || 'unassigned'}
              onValueChange={(value) => onAssetManagerChange(value === 'unassigned' ? '' : value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {users.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Portfolio *">
            <Select
              value={portfolioSelectValue}
              onValueChange={(value) => {
                if (value === 'unselected') {
                  onPortfolioChange('');
                  return;
                }
                if (value.startsWith('legacy-portfolio::')) {
                  onChange('portfolio_id', '');
                  return;
                }
                onPortfolioChange(value);
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select portfolio" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unselected">Select portfolio</SelectItem>
                {form.portfolio && !hasPortfolioNameInOptions && (
                  <SelectItem value={portfolioLegacyValue}>
                    {form.portfolio} (Current)
                  </SelectItem>
                )}
                {portfolios.map((portfolio) => (
                  <SelectItem key={portfolio.id} value={portfolio.id}>
                    {portfolio.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Sub-portfolio *">
            <Select
              value={subPortfolioSelectValue}
              onValueChange={(value) => {
                if (value === 'unselected') {
                  onSubPortfolioChange('');
                  return;
                }
                if (value.startsWith('legacy-sub-portfolio::')) {
                  onChange('sub_portfolio_id', '');
                  return;
                }
                onSubPortfolioChange(value);
              }}
              disabled={!resolvedPortfolioId && !form.portfolio}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select sub-portfolio" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unselected">Select sub-portfolio</SelectItem>
                {form.sub_portfolio && !hasSubPortfolioNameInOptions && (
                  <SelectItem value={subPortfolioLegacyValue}>
                    {form.sub_portfolio} (Current)
                  </SelectItem>
                )}
                {availableSubPortfolios.map((subPortfolio) => (
                  <SelectItem key={subPortfolio.id} value={subPortfolio.id}>
                    {subPortfolio.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="DC Installed Capacity (kWp) *">
            <Input
              type="number"
              value={form.dc_installed_capacity_kwp}
              onChange={(e) => onChange('dc_installed_capacity_kwp', e.target.value)}
            />
          </Field>
          <Field label="Export Capacity (kVA)">
            <Input
              type="number"
              value={form.export_capacity_kva}
              onChange={(e) => onChange('export_capacity_kva', e.target.value)}
            />
          </Field>
          <Field label="Import Capacity (kVA)">
            <Input
              type="number"
              value={form.import_capacity_kva}
              onChange={(e) => onChange('import_capacity_kva', e.target.value)}
            />
          </Field>
        </div>
      </div>

      <div className="space-y-4 border-t border-slate-100 pt-6">
        <div>
          <h3 className="font-heading text-lg font-semibold text-slate-900">Companies</h3>
          <p className="text-sm text-slate-500">Keep the primary entity and counterparties with contact coverage.</p>
        </div>
        <div className="space-y-5">
          {companySections.map((section) => {
            const matchingCompanies = companies.filter(
              (record) => record.company_type === section.companyType && record.status !== 'inactive'
            );

            return (
              <div key={section.key} className="rounded-xl border border-slate-200 p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                  <Field label={section.label}>
                    <Select
                      value={form[`${section.key}_company_id`] || 'unselected'}
                      onValueChange={(value) => onCompanyChange(section.key, value === 'unselected' ? '' : value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder={`Select ${section.label}`} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unselected">Select {section.label}</SelectItem>
                        {matchingCompanies.map((company) => (
                          <SelectItem key={company.id} value={company.id}>
                            {company.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Contact Person">
                    <Input value={form[`${section.key}_contact_person`]} readOnly />
                  </Field>
                  <Field label="Contact Email">
                    <Input type="email" value={form[`${section.key}_contact_email`]} readOnly />
                  </Field>
                  <Field label="Contact Phone">
                    <Input value={form[`${section.key}_contact_phone`]} readOnly />
                  </Field>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="space-y-4 border-t border-slate-100 pt-6">
        <div>
          <h3 className="font-heading text-lg font-semibold text-slate-900">Address</h3>
          <p className="text-sm text-slate-500">Use the site office or registered project address.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <Field label="Address Line" className="xl:col-span-3">
            <Input value={form.address_line} onChange={(e) => onChange('address_line', e.target.value)} />
          </Field>
          <Field label="Country">
            <Combobox 
              options={countries} 
              value={form.country} 
              onChange={handleCountryChange} 
              placeholder="Search country..." 
              emptyText="No country found"
            />
          </Field>
          <Field label="State / Region">
            <Combobox 
              options={statesList} 
              value={form.state_region} 
              onChange={handleStateChange} 
              placeholder="Search state..." 
              emptyText="No state found"
              disabled={!form.country}
            />
          </Field>
          <Field label="City">
            <Combobox 
              options={citiesList} 
              value={form.city} 
              onChange={handleCityChange} 
              placeholder="Search city..." 
              emptyText="No city found"
              disabled={!form.state_region}
            />
          </Field>
          <Field label="Pincode">
            <Input value={form.pincode} onChange={(e) => onChange('pincode', e.target.value)} />
          </Field>
          <Field label="Latitude">
            <Input value={form.latitude} onChange={(e) => onChange('latitude', e.target.value)} />
          </Field>
          <Field label="Longitude">
            <Input value={form.longitude} onChange={(e) => onChange('longitude', e.target.value)} />
          </Field>
        </div>
      </div>

      <div className="space-y-4 border-t border-slate-100 pt-6">
        <div>
          <h3 className="font-heading text-lg font-semibold text-slate-900">Key Dates</h3>
          <p className="text-sm text-slate-500">These dates support handover, contracts, and commercial tracking.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {keyDateFields.map((field) => (
            <Field key={field.key} label={field.label}>
              <Input
                type="date"
                value={form[field.key]}
                onChange={(e) => onChange(field.key, e.target.value)}
              />
            </Field>
          ))}
        </div>
      </div>
    </div>
  );
}
